import { Result } from '@cplug/frontend-core/utils'
import { BaseGatewayErrorDTO, type DataWrappedDTO } from '@cplug/frontend-core/gateways'
import { isSuccessResponse, ErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { HttpStatusCode } from 'axios'
import type { Company } from '../models/Company'
import { type CompanyDTO, CompanyDTO as CompanyDTOSchema, mapCompanyToDTO, mapDTOToCompany } from './DTOs/CompanyDTO'
import { mapDTOToCompanyApiToken } from './DTOs/CompanyApiTokenDTO'
import { CPBaseCRUDGateway } from '~/shared/gateways/CPBaseCRUDGateway'

type ListDTO = { companies: CompanyDTO[] }

export class CPCompanyGateway extends CPBaseCRUDGateway<CompanyDTO, ListDTO, Company> {
  protected readonly apiUrlDomain = 'companies'
  protected readonly dtoToModelMapper = mapDTOToCompany
  // @ts-expect-error - mapper to request dto
  protected readonly modelToDtoMapper = mapCompanyToDTO

  protected get listField (): keyof ListDTO { return 'companies' }

  protected get modelIdField (): keyof Company { return 'id' }

  protected dtoSchema = CompanyDTOSchema

  async updatePhoto (id: number, image: Blob | string): Promise<Result<undefined>> {
    const formData = new FormData()
    formData.append('image', image)

    const response = await this.post<DataWrappedDTO<Company> | BaseGatewayErrorDTO, FormData>(
      `${this.apiUrlDomain}/${id}/image`,
      formData
    )

    const responseStatus = response?.status ?? HttpStatusCode.InternalServerError

    if (responseStatus >= HttpStatusCode.MultipleChoices) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(undefined)
  }

  async revokeAppAccess (id: number, password: string): Promise<Result<undefined>> {
    const response = await this.post<DataWrappedDTO<Company> | BaseGatewayErrorDTO, { password: string }>(
      `${this.apiUrlDomain}/${id}/revoke-api-token`,
      { password }
    )

    const responseStatus = response?.status ?? HttpStatusCode.InternalServerError

    if (responseStatus >= HttpStatusCode.MultipleChoices) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(undefined)
  }

  async getApiToken (id: number, password: string): Promise<Result<string>> {
    const response = await this.post<DataWrappedDTO<{ token: string }>>(
      `${this.apiUrlDomain}/${id}/get-api-token`,
      { password }
    )

    if (response?.data == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(mapDTOToCompanyApiToken(response?.data?.data))
  }
}
